import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "../components/Layout";
import { Reveal, Label, GoldLink, Diamond } from "../components/Reveal";
import { FOUNDER_IMG } from "../i18n";

const AboutPage = () => {
  const { lang, t } = useLang();
  useEffect(() => {
    document.title = t.pageTitles.about;
    document.documentElement.lang = lang;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", t.pageDescs.about);
  }, [lang, t]);

  return (
    <>
      <section data-testid="about-hero" className="border-b border-white/10 pt-40 pb-20 md:pb-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal y={24}>
            <Label testId="about-label">{t.about.label}</Label>
            <h1 className="mt-6 max-w-4xl font-serif text-4xl sm:text-6xl font-light leading-[1.08] text-white">
              {t.about.title}
            </h1>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-bio-section" className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="relative mx-auto max-w-md overflow-hidden border border-brand-gold/30 bg-gradient-to-b from-brand-elevated to-brand-ink p-2 shadow-2xl">
                <img
                  data-testid="about-founder-photo"
                  src={FOUNDER_IMG}
                  alt="Rodrigue Cassildé"
                  className="aspect-[4/5] w-full object-cover grayscale-[20%]"
                />
                <div className="pointer-events-none absolute inset-2 bg-gradient-to-t from-brand-ink/60 via-transparent to-transparent" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <Label testId="about-bio-label">{t.about.bioLabel}</Label>
              <h2 className="mt-6 font-serif text-4xl font-light text-white">Rodrigue Cassildé</h2>
              {t.about.bio.map((p, i) => (
                <p key={i} className="mt-6 text-base font-light leading-relaxed text-white/60">
                  {p}
                </p>
              ))}
              <blockquote
                data-testid="about-quote"
                className="mt-10 border-l-2 border-brand-gold pl-6 font-serif text-xl font-light italic leading-relaxed text-white/75"
              >
                {t.about.quote}
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-values-section" className="border-b border-white/10 bg-brand-surface/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Label testId="about-values-label">{t.about.valuesLabel}</Label>
            <h2 className="mt-6 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight text-white">
              {t.about.valuesTitle}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {t.about.values.map((v, i) => (
              <Reveal key={v.num} delay={i * 0.12}>
                <div
                  data-testid={`value-card-${v.num}`}
                  className="h-full border border-white/10 bg-brand-ink p-8 md:p-10 transition-all duration-300 hover:border-brand-gold/50 hover:-translate-y-1"
                >
                  <span className="font-serif text-5xl font-light text-brand-gold/40">{v.num}</span>
                  <h3 className="mt-8 font-serif text-2xl font-light text-white">{v.name}</h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-white/55">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-legal-section" className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20">
          <Reveal>
            <Label testId="about-legal-label">{t.about.legalLabel}</Label>
            <p data-testid="about-legal-text" className="mt-6 max-w-3xl text-sm font-light leading-relaxed text-white/50">
              {t.about.legal}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-cta-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-36">
          <Reveal className="text-center">
            <Diamond className="text-sm" />
            <h2 className="mx-auto mt-8 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight text-white">
              {t.about.ctaTitle}
            </h2>
            <div className="mt-12 flex justify-center">
              <GoldLink testId="about-cta-button" to={`/${lang}/bilan`}>
                {t.about.cta} <ArrowRight size={16} />
              </GoldLink>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
